import { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import FilterBtn from "../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import CustomLoadingOverlay from "../../../Components/CustomLoadingOverlay";
import { createFilterHandler } from "../../../Utils/FilterUtils";
import httpclient from "../../../Utils";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import AddBayPullingScheduleDialog from "./AddBayPullingScheduleDialog";

import { getColumns } from "./columns";
import Filter from "./FilterDialog";

const INITIAL_FILTER_STATE = {
  bay: "",
  time: "",
};

const fieldMappings = {
  bay: ["bay", "$eq"],
  time: ["time", "$eq"],
};

export default function BayPullingSchedule() {
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [bayList, setBayList] = useState([]);
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([]);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const response = await httpclient.get("/bay-pulling-schedule", {
        params: {
          page: params.page + 1,
          per_page: params.pageSize,
          ...params.filters,
        },
      });
      
      if (response.data.status === 200) {
        // Store bayList for dropdown
        setBayList(response.data.bayList || []);
        
        // Map the API response data to match column fields
        const mappedData = response.data.data.map((item) => ({
          id: item.id,
          bay: item["bay "] || item.bay, // Handle space in property name
          time: item.time,
          created_at: item.created_at,
          created_by: item["created_by "] || item.created_by, // Handle space in property name
        }));
        
        setData(mappedData);
        setRowCount(response.data.meta.total);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch on component mount
  useEffect(() => {
    fetchData({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      filters: submittedData,
    });
  }, []);

  const handleDeleteSchedule = async () => {
    setIsDeleting(true);
    
    try {
      const response = await httpclient.post(`/bay-pulling-schedule/delete/${selectedSchedule.id}`);
      
      if (response.data.status === 200) {
        showNotification("success", response.data.message);
        setOpenDeleteDialog(false);
        setSelectedSchedule(null);
        // Refresh the data
        fetchData({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          filters: submittedData,
        });
      } else {
        showNotification("info", response.data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: (results) => {
      // Map the filtered results
      const mappedResults = results.map((item) => ({
        id: item.id,
        bay: item["bay "] || item.bay,
        time: item.time,
        created_at: item.created_at,
        created_by: item["created_by "] || item.created_by,
      }));
      setData(mappedResults);
    },
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `/bay-pulling-schedule`,
    fieldMappings,
    setRowCount,
    refresh: (params) => fetchData(params),
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    newFilterData[field] = "";

    delete newSubmittedData[field];
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(INITIAL_FILTER_STATE);
  };

  const handleAddSchedule = () => {
    setOpenAddDialog(true);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    fetchData({
      page: newModel.page,
      pageSize: newModel.pageSize,
      filters: submittedData,
    });
  };

  const columns = getColumns(setSelectedSchedule, setOpenDeleteDialog);

  const deleteMessage = selectedSchedule ? (
    <>
      Are you sure you want to delete bay pulling schedule for bay{" "}
      <strong>{selectedSchedule.bay}</strong> at time{" "}
      <strong>{selectedSchedule.time}</strong>?
    </>
  ) : (
    "Are you sure you want to delete this bay pulling schedule?"
  );

  return (
    <>
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 16px",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bay Pulling Schedule
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  borderRadius: "4px",
                  marginRight: "16px",
                 
                  backgroundColor: "#7152F3",
                  "&:hover": {
                    backgroundColor: "#5c34fa",
                  },
                }}
                startIcon={<AddIcon sx={{ marginRight: 0 }} />}
                onClick={handleAddSchedule}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                  }}
                >
                  Add New Schedule
                </Typography>
              </Button>
              <img
                src={filterOpen ? FilterBtnUp : FilterBtn}
                alt="Filter"
                style={{ cursor: "pointer" }}
                onClick={() => setFilterOpen(!filterOpen)}
              />
            </Box>
          </Box>

          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
            bayList={bayList}
          />

          <DataGrid
           style= {{marginTop : "25px"}}
            rows={data || []}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            slots={{
              loadingOverlay: CustomLoadingOverlay,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#48505E",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F8F9FA",
                color: "#1A1C1E",
                fontWeight: 600,
              },
            }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[20, 50, 100]}
          />
        </Box>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        title="Delete Schedule"
        message={deleteMessage}
        onConfirm={handleDeleteSchedule}
        loading={isDeleting}
      />
      
      {/* Add Dialog Component */}
      <AddBayPullingScheduleDialog
        open={openAddDialog}
        handleClose={() => setOpenAddDialog(false)}
        refresh={() => fetchData({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          filters: submittedData,
        })}
        bayList={bayList}
      />
    </>
  );
}